import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Sparkles, Loader2, ImagePlus, Wand2, FolderOpen } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = ['auto', 'realistic', 'toon', 'pixel'];
const TYPES = ['sprite', 'texture', 'model'];

/**
 * Compact Assets panel used inside the Editor right sidebar.
 * Users can generate an asset OR switch to their whole library and drag
 * any of their previously-generated, imported, or uploaded assets onto the scene.
 */
export default function AssetsPanel({ projectId, onDropToScene }) {
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('auto');
  const [type, setType] = useState('sprite');
  const [busy, setBusy] = useState(false);
  const [assets, setAssets] = useState([]);
  const [library, setLibrary] = useState([]);
  const [source, setSource] = useState('project'); // 'project' | 'library'
  const [castRole, setCastRole] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [remixing, setRemixing] = useState(null);
  const [importing, setImporting] = useState(false);

  // Upload an external image → it lands in the project asset list ready to drag into the scene.
  const importImage = async (file) => {
    if (!file || importing) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'image');
      if (projectId) fd.append('project_id', projectId);
      const r = await axios.post(`${API}/uploads`, fd, { withCredentials: true });
      const up = r.data?.upload;
      if (up) {
        const item = { asset_id: up.upload_id, url: up.url, name: up.filename, asset_type: 'auto', prompt: up.filename, _source: 'upload' };
        setAssets(prev => [item, ...prev]);
        setLibrary(prev => [item, ...prev]);
        toast.success(`"${up.filename}" imported — drag it into the scene`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setImporting(false); }
  };

  // Persist the chosen cast role onto the asset itself — revokes/overrides AI-given roles.
  const persistRole = async (a) => {
    const role = castRole === 'auto' ? '' : castRole;
    try {
      await axios.patch(`${API}/assets/${a.asset_id}/role`, { role }, { withCredentials: true });
      const patch = (list) => list.map(x => x.asset_id === a.asset_id ? { ...x, role: role || undefined } : x);
      setAssets(patch);
      setLibrary(patch);
      toast.success(role ? `"${a.name}" is now cast as ${role}` : `Role revoked from "${a.name}"`);
    } catch {
      toast.error('Role update failed');
    }
  };

  const loadProjectAssets = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assets/${projectId}`, { withCredentials: true });
      setAssets(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const loadLibrary = async () => {
    setLoading(true);
    try {
      const [uploadsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/uploads/mine?kind=image`, { withCredentials: true }).catch(() => ({ data: { uploads: [] } })),
        axios.get(`${API}/assets/mine`, { withCredentials: true }).catch(() => ({ data: { assets: [] } })),
      ]);
      const uploads = (uploadsRes.data?.uploads || []).map(u => ({
        asset_id: u.upload_id, url: u.url, name: u.filename, asset_type: u.kind === 'image' ? 'auto' : 'sprite',
        prompt: u.filename, _source: 'upload',
      }));
      const gen = (assetsRes.data?.assets || []).map(a => ({
        asset_id: a.asset_id, url: a.url, name: a.name || a.prompt?.slice(0, 40) || 'asset',
        asset_type: a.asset_type || 'sprite', prompt: a.prompt,
        _source: a.imported_from ? 'imported' : 'generated',
      }));
      setLibrary([...gen, ...uploads]);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (source === 'project') loadProjectAssets();
    else loadLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, source]);

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/assets/generate`,
        { prompt: prompt.trim(), style, type, project_id: projectId },
        { withCredentials: true }
      );
      setAssets(prev => [r.data.asset, ...prev]);
      setPrompt('');
      toast.success('Asset generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  const remix = async (a) => {
    if (remixing) return;
    setRemixing(a.asset_id);
    try {
      const r = await axios.post(`${API}/assets/${a.asset_id}/remix`, {}, { withCredentials: true });
      if (source === 'project') setAssets(prev => [r.data.asset, ...prev]);
      else setLibrary(prev => [{
        asset_id: r.data.asset.asset_id, url: r.data.asset.url,
        name: r.data.asset.name, asset_type: r.data.asset.asset_type,
        prompt: r.data.asset.prompt, _source: 'generated',
      }, ...prev]);
      toast.success('Remix ready');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remix failed');
    } finally { setRemixing(null); }
  };

  const displayed = source === 'project' ? assets : library;

  return (
    <div data-testid="editor-assets-panel" className="border-t border-[#1E1E24]">
      <div className="editor-panel-header flex items-center gap-2">
        <Sparkles className="w-3 h-3 text-[#06B6D4]" />
        Assets
        <span className="ml-auto text-[9px] text-zinc-600 font-mono">DRAG TO SCENE</span>
      </div>
      {/* Source toggle */}
      <div className="flex gap-1 px-3 pt-2">
        <button
          onClick={() => setSource('project')}
          className={`flex-1 text-[10px] font-mono uppercase py-1 rounded-sm border ${source === 'project' ? 'bg-[#06B6D4] text-black border-[#06B6D4]' : 'border-[#1E1E24] text-zinc-400 hover:text-white'}`}
          data-testid="assets-source-project"
        >
          <Sparkles className="w-2.5 h-2.5 inline mr-1" /> Project
        </button>
        <button
          onClick={() => setSource('library')}
          className={`flex-1 text-[10px] font-mono uppercase py-1 rounded-sm border ${source === 'library' ? 'bg-[#06B6D4] text-black border-[#06B6D4]' : 'border-[#1E1E24] text-zinc-400 hover:text-white'}`}
          data-testid="assets-source-library"
        >
          <FolderOpen className="w-2.5 h-2.5 inline mr-1" /> My Library
        </button>
      </div>
      <div className="p-3 space-y-2">
        {source === 'project' && (
          <>
            <Input
              placeholder="e.g. brave elf ranger hero"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && generate()}
              className="h-8 bg-transparent border-[#1E1E24] text-white text-xs"
              disabled={busy}
              data-testid="editor-asset-prompt"
            />
            <div className="grid grid-cols-2 gap-1">
              <select value={type} onChange={(e) => setType(e.target.value)} className="bg-[#121214] border border-[#1E1E24] rounded-sm px-1.5 h-7 text-[11px] text-white uppercase" disabled={busy} data-testid="editor-asset-type">
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={style} onChange={(e) => setStyle(e.target.value)} className="bg-[#121214] border border-[#1E1E24] rounded-sm px-1.5 h-7 text-[11px] text-white uppercase" disabled={busy} data-testid="editor-asset-style">
                {STYLES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Button onClick={generate} disabled={busy || !prompt.trim()} className="w-full h-8 bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 text-xs" data-testid="editor-asset-generate">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ImagePlus className="w-3 h-3 mr-1" /> Generate</>}
            </Button>
            <label className="block">
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                disabled={importing}
                onChange={(e) => importImage(e.target.files?.[0])}
                data-testid="editor-asset-import-input"
              />
              <span
                className="w-full h-8 flex items-center justify-center gap-1 border border-dashed border-[#1E1E24] rounded-md text-[11px] text-zinc-400 hover:text-white hover:border-[#06B6D4] cursor-pointer transition-colors"
                data-testid="editor-asset-import-btn"
              >
                {importing ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderOpen className="w-3 h-3" />} Import your own image
              </span>
            </label>
          </>
        )}

        {loading ? (
          <div className="py-2 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-[#06B6D4]" /></div>
        ) : displayed.length === 0 ? (
          <p className="text-[10px] text-zinc-600 text-center py-2">
            {source === 'library' ? 'Library empty — upload files at /library or import from Marketplace' : 'No assets generated yet'}
          </p>
        ) : (
          <>
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-zinc-500 font-mono uppercase whitespace-nowrap">Cast as</span>
            <select value={castRole} onChange={(e) => setCastRole(e.target.value)} className="flex-1 bg-[#121214] border border-[#1E1E24] rounded-sm px-1 h-6 text-[10px] text-white" data-testid="assets-cast-role">
              {['auto', 'player', 'enemy', 'collectible', 'goal', 'platform', 'weapon', 'texture', 'background', 'decoration'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-1 pt-1 max-h-[280px] overflow-y-auto">
            {displayed.slice(0, 60).map(a => (
              <div
                key={a.asset_id}
                draggable
                onDragStart={(e) => {
                  const payload = { asset_id: a.asset_id, url: a.url, name: a.name, asset_type: a.asset_type, role: castRole !== 'auto' ? castRole : a.role };
                  e.dataTransfer.setData('application/x-playmold-asset', JSON.stringify(payload));
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onDoubleClick={() => onDropToScene?.(castRole !== 'auto' ? { ...a, role: castRole } : a)}
                title={a.prompt || a.name}
                className="aspect-square bg-[#121214] border border-[#1E1E24] hover:border-[#06B6D4] rounded-sm overflow-hidden relative group cursor-grab active:cursor-grabbing"
                data-testid={`editor-asset-${a.asset_id}`}
              >
                <img src={a.url} alt={a.prompt || a.name} className="w-full h-full object-cover" loading="lazy" draggable={false} />
                {a._source && (
                  <span className={`absolute top-0.5 left-0.5 text-[7px] font-mono px-1 rounded ${a._source === 'generated' ? 'bg-purple-500/80 text-white' : a._source === 'imported' ? 'bg-amber-500/80 text-black' : 'bg-black/60 text-zinc-300'}`}>
                    {a._source === 'generated' ? 'AI' : a._source === 'imported' ? 'MKT' : 'UP'}
                  </span>
                )}
                {a.role && (
                  <span className="absolute top-0.5 right-0.5 text-[7px] font-mono px-1 rounded bg-[#06B6D4]/80 text-black uppercase" data-testid={`asset-role-badge-${a.asset_id}`}>
                    {a.role}
                  </span>
                )}
                {/* Explicit tap-to-add button — critical for mobile where HTML5 drag is unreliable */}
                <button
                  onClick={(e) => { e.stopPropagation(); onDropToScene?.(castRole !== 'auto' ? { ...a, role: castRole } : a); }}
                  className="absolute bottom-0.5 left-0.5 h-5 px-1.5 rounded-sm bg-[#06B6D4] text-black text-[8px] font-bold flex items-center justify-center opacity-90 hover:opacity-100"
                  data-testid={`editor-asset-add-${a.asset_id}`}
                  title="Add this asset to the scene"
                >
                  + SCENE
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); persistRole(a); }}
                  className="absolute top-0.5 right-0.5 mt-3 h-4 px-1 rounded-sm bg-black/70 text-[#67E8F9] text-[7px] font-bold opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`editor-asset-setrole-${a.asset_id}`}
                  title={castRole === 'auto' ? 'Revoke this asset\'s role' : `Save role "${castRole}" on this asset`}
                >
                  {castRole === 'auto' ? 'CLEAR ROLE' : `SET ${castRole.toUpperCase()}`}
                </button>
                {source === 'project' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); remix(a); }}
                    disabled={remixing === a.asset_id}
                    className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-sm bg-black/70 backdrop-blur flex items-center justify-center text-white hover:text-[#06B6D4] opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remix"
                    data-testid={`editor-asset-remix-${a.asset_id}`}
                  >
                    {remixing === a.asset_id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Wand2 className="w-2.5 h-2.5" />}
                  </button>
                )}
              </div>
            ))}
          </div>
          </>
        )}
      </div>
    </div>
  );
}
